import React from 'react';
import { Button, Switch, TextInput } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan, faCopy } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { AreaTableView } from '../../../../../services/areas/areas.service';
import { useTableEditToolbarController } from './useTableEditToolbarController';
import styles from './TableEditToolbar.module.scss';

type TableEditToolbarProps = {
  table: AreaTableView;
  compact?: boolean;
  onRename: (name: string) => void;
  onChangeShape: (shape: AreaTableView['shape']) => void;
  onChangeFreeTable?: (isFree: boolean) => void;
  isSavingTable?: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
};

const TableEditToolbarComponent: React.FC<TableEditToolbarProps> = ({
  table,
  compact,
  onRename,
  onChangeShape,
  onChangeFreeTable,
  isSavingTable = false,
  onDuplicate,
  onDelete,
}) => {
  const {
    isBelow,
    nameDraft,
    setNameDraft,
    commitName,
    handleNameKeyDown,
    anchorStyle,
  } = useTableEditToolbarController({ table, onRename });

  return (
    <div
      className={`${styles.toolbar} ${isBelow ? styles.isBelow : ''} ${compact ? styles.compact : ''}`}
      style={anchorStyle}
      data-testid="table-edit-toolbar"
      role="toolbar"
      aria-label="עריכת שולחן"
    >
      <div className={styles.actions}>
      {/* Table number / name */}
      <TextInput
        unstyled
        data-testid="table-edit-toolbar-name"
        classNames={{ input: styles.nameField }}
        styles={{ input: { width: `${Math.max(2, nameDraft.length + 1)}ch` } }}
        value={nameDraft}
        onChange={(event) => setNameDraft(event.currentTarget.value)}
        onBlur={commitName}
        onKeyDown={handleNameKeyDown}
        maxLength={30}
        aria-label="מספר שולחן"
      />

      <span className={styles.separator} />

      {/* Shape toggle */}
      <Button
        unstyled
        type="button"
        data-testid="table-edit-toolbar-shape-square"
        classNames={{ root: `${styles.button} ${table.shape === 'square' ? styles.buttonActive : ''}`, inner: styles.buttonInner, label: styles.buttonInner }}
        onClick={() => onChangeShape('square')}
        aria-label="שולחן מרובע"
        aria-pressed={table.shape === 'square'}
      >
        <span className={`${styles.glyph} ${styles.glyphSquare}`} />
      </Button>
      <Button
        unstyled
        type="button"
        data-testid="table-edit-toolbar-shape-circle"
        classNames={{ root: `${styles.button} ${table.shape === 'circle' ? styles.buttonActive : ''}`, inner: styles.buttonInner, label: styles.buttonInner }}
        onClick={() => onChangeShape('circle')}
        aria-label="שולחן עגול"
        aria-pressed={table.shape === 'circle'}
      >
        <span className={`${styles.glyph} ${styles.glyphCircle}`} />
      </Button>

      <span className={styles.separator} />

      {/* Duplicate */}
      <Button unstyled type="button" data-testid="table-edit-toolbar-duplicate" classNames={{ root: styles.button, inner: styles.buttonInner, label: styles.buttonInner }} onClick={onDuplicate} aria-label="שכפל שולחן">
        <FontAwesomeIcon icon={faCopy as IconProp} />
      </Button>

      {/* Delete */}
      <Button
        unstyled
        type="button"
        data-testid="table-edit-toolbar-delete"
        classNames={{ root: `${styles.button} ${styles.buttonDanger}`, inner: styles.buttonInner, label: styles.buttonInner }}
        onClick={onDelete}
        aria-label="מחק שולחן"
      >
        <FontAwesomeIcon icon={faTrashCan as IconProp} />
      </Button>
      </div>

      <span className={`${styles.caret} ${isBelow ? styles.caretBelow : ''}`} />
      {onChangeFreeTable && (
        <div className={styles.freeTableRow}>
          <Switch label="שולחן חינם" checked={table.isFree ?? false}
            onChange={(event) => onChangeFreeTable(event.currentTarget.checked)}
            disabled={isSavingTable} data-testid="table-edit-toolbar-free" />
        </div>
      )}
    </div>
  );
};

export const TableEditToolbar = React.memo(TableEditToolbarComponent);
